import { RiMoreFill } from "@remixicon/react";
import classNames from "classnames";
import React, { ReactNode, useCallback, useState } from "react";
import { useDroppable } from "@dnd-kit/core";
import { DashboardGroupBy, DashboardView } from "../../../../__generated__/api";
import { ReactComponent as ArrowDown } from "../../../../assets/img/arrow-down.svg";
import { useTicketState } from "../../../../contexts/ticket-state/TicketState";
import { TicketItem, TicketColumn } from "../../../../contexts/ticket-state";
import Button from "../../../theme/button/Button";
import ColumnGroup from "../column-group/ColumnGroup";
import Styles from "./Column.module.scss";
import ColumnGroupEmpty from "../column-group/ColumnGroupEmpty";
import { ActionContext, ActionType } from "../../../../contexts/ticket-state/action-registry";
import Dropdown from "../../../theme/dropdown/Dropdown";
import ScrollableList from "../../../scrollable-list/ScrollableList";

const MAX_COLORS = 8;

export interface ColumnProps {
    ticketState: TicketColumn;
    columnIndex: number;
    draggingItem: TicketItem | undefined;
}
export default function Column({
    ticketState,
    columnIndex,
    draggingItem,
}: ColumnProps) {
    const ticketContext = useTicketState();
    const columnRef = ticketState.useDisplay();

    const [isMenuExpanded, setMenuExpanded] = useState(false);

    const { unreadCount } = ticketState;
    const { settings } = ticketContext.currentDashboard;
    const view = settings?.view ?? DashboardView.Kanban;
    const isGrouping = settings?.groupBy && settings.groupBy !== DashboardGroupBy.NoGroup;

    const ticketStateId = ticketState.ticketState.id;
    const { setNodeRef, over } = useDroppable({
        id: ticketStateId,
    });

    const handleScroll = useCallback(() => ticketContext.loadMoreResults(), [ticketContext]);

    let body: ReactNode;
    if (ticketState.groups.isEmpty) {
        body = <ColumnGroupEmpty />;
    } else if (isGrouping) {
        body = (
            <ScrollableList onScroll={handleScroll} isFragment threshold={0.5} loadingText="Loading more...">
                {ticketState.groups.map(group => (
                    <ColumnGroup
                        key={group.group?.id ?? "default"}
                        group={group}
                        draggingItem={draggingItem}
                    />
                ))}
            </ScrollableList>
        );
    } else {
        body = (
            <ScrollableList onScroll={handleScroll} isFragment threshold={0.5} loadingText="Loading more...">
                <ColumnGroup
                    group={ticketState.groups.firstElement}
                    draggingItem={draggingItem}
                />
            </ScrollableList>
        );
    }

    const markColumnRead = () => {
        ticketContext.selectionState.execute(ActionType.MARK_READ, ticketState.unreadItems, ActionContext.BULK_ACTION);
        ticketContext.selectionState.update();
    };

    const save = () => {
        ticketContext.saveDashboard({
            ...ticketContext.currentDashboard,
            ticketStates: ticketContext.currentDashboard.ticketStates.filter(row => row.ticketStateId !== ticketStateId),
        });
    };

    const toggleGroups = () => {
        const hasCollapsed = ticketState.groups.some(group => group.isCollapsed);
        ticketState.groups.forEach(group => group.setIsVisible(hasCollapsed));
    };

    return (
        <div
            ref={columnRef}
            className={classNames(
                "column",
                Styles.column,
                Styles[`column-${view?.toLowerCase()}`],
                ticketState.groups.isEmpty ? "column-empty" : "",
            )}
        >
            <div className={classNames("column-header", `color-${(columnIndex % MAX_COLORS) + 1}`, "d-flex")}>
                <div
                    className="flex-grow-1 align-items-center d-flex text-body-1-medium column-title p3e-2"
                    title={ticketState.ticketState.name}
                >
                    <span className="column-text">
                        {ticketState.ticketState.name}
                    </span>
                    {" "}
                    <span className="ticket-count">
                        {ticketState.ticketCount}
                        {ticketContext.hasMoreResults && ticketState.ticketCount > 0 ? "+" : ""}
                    </span>

                    {unreadCount > 0 && (
                        <div className="btn-group m3s-105 unread-count" role="group" aria-label="Unreads button">
                            <Button
                                variant="danger"
                                size="xsmall"
                                className="p3e-0"
                                onClick={() => {
                                    ticketState.unreadItems.firstElement?.focus();
                                }}
                            >
                                <ArrowDown />
                                {" "}
                                {unreadCount > 99 ? "99+" : unreadCount}
                                {" "}
                                Unread
                            </Button>
                            <Button size="xsmall" variant="danger" isClose onClick={markColumnRead} />
                        </div>

                    )}

                </div>

                <Dropdown onToggle={nextShow => setMenuExpanded(nextShow)} show={isMenuExpanded}>
                    <Dropdown.Toggle
                        variant="none"
                        className="p3-0"
                        prefixIcon={<RiMoreFill className="icon-secondary" />}
                    />
                    <Dropdown.Menu>
                        {isMenuExpanded && (
                            <>
                                {settings?.view === DashboardView.Kanban && isGrouping && ticketState.ticketCount > 0 && (
                                    <Dropdown.Item onClick={toggleGroups}>
                                        {ticketState.groups.some(group => group.isCollapsed) ? "Expand All" : "Collapse All"}
                                    </Dropdown.Item>
                                )}
                                <Dropdown.Item onClick={save}>
                                    Hide
                                </Dropdown.Item>
                            </>
                        )}

                    </Dropdown.Menu>
                </Dropdown>
            </div>

            <div className="column-body" ref={setNodeRef}>
                <div className="column-body-contents">
                    {draggingItem && draggingItem?.ticketStateId !== ticketStateId ? (
                        <div className={classNames("column-drop", over?.id === ticketStateId ? "active" : "")}>
                            Move to
                            {" "}
                            {ticketState.ticketState.name}
                        </div>
                    ) : body }
                </div>
            </div>
        </div>
    );
}
